import React from "react";
import type { IconTextBlockData } from "../../../types/content";
import { imageSizeAttributes } from "@/utils/imageSize";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./IconTextBlock.module.css";

/**
 * The image's max width per icon size; a large one fills the 600px block. The
 * block can sit in a narrower grid cell, so the lazy image leads with `auto`
 * and the browser uses its laid-out width where it supports `sizes=auto`.
 */
const ICON_SIZES = {
  small: "auto, (max-width: 640px) 160px, 200px",
  medium: "auto, (max-width: 640px) 220px, 280px",
  large: "auto, (max-width: 640px) 100vw, 600px",
} as const;

export interface IconTextBlockProps {
  block: IconTextBlockData;
  className?: string;
}

/**
 * Icon Text Block component - displays image/icon with heading and body text
 * Commonly used for FAQ-style questions, feature highlights, and value propositions
 * Supports centered (default) and left-aligned layouts
 */
export const IconTextBlock: React.FC<IconTextBlockProps> = ({
  block,
  className,
}) => {
  const {
    icon,
    heading,
    bodyText,
    textAlignment = "center",
    iconSize = "medium",
    headingColor,
    bodyTextColor,
  } = block;

  if (!heading) {
    return null;
  }

  const containerClasses = [
    styles.block,
    styles[
      `align${textAlignment.charAt(0).toUpperCase() + textAlignment.slice(1)}`
    ],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className={containerClasses}
      data-component="icon-text-block"
      data-alignment={textAlignment}
      data-icon-size={iconSize}
    >
      {/* Icon/Image */}
      {icon && (
        <div className={styles.iconWrapper} data-size={iconSize}>
          <img
            {...responsiveCmsImage(
              icon.url,
              // An unknown size gets no max-width in the CSS, like large.
              ICON_SIZES[iconSize] ?? ICON_SIZES.large,
              icon.width
            )}
            alt=""
            aria-hidden="true"
            className={styles.icon}
            loading="lazy"
            {...imageSizeAttributes(icon)}
          />
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        <h3
          className={styles.heading}
          style={headingColor ? { color: headingColor } : undefined}
        >
          {heading}
        </h3>
        {bodyText && (
          <p
            className={styles.bodyText}
            style={bodyTextColor ? { color: bodyTextColor } : undefined}
          >
            {bodyText}
          </p>
        )}
      </div>
    </article>
  );
};

export default IconTextBlock;
